"use client";

import { useTranslations } from 'next-intl';
import Image from 'next/image';

import config from '@/config';

interface TopSponsorBannerProps {
  locale: string;
}

export default function TopSponsorBanner({ locale }: TopSponsorBannerProps) {
  const t = useTranslations('TopSponsorBanner');

  // Don't render if disabled in config
  if (!config.sponsorBanner?.enabled) {
    return null;
  }

  return (
    <aside 
      role="complementary" 
      aria-label={`${t('sponsored')} ${config.sponsorBanner.sponsorName}`}
      data-locale={locale}
      className="bg-white border-b border-gray-200 shadow-sm relative z-50"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center flex-1 min-w-0 gap-3">
            {/* Logo */}
            {config.sponsorBanner.logo && (
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-50 rounded-md flex items-center justify-center border border-gray-200">
                  <Image 
                    src={config.sponsorBanner.logo}
                    alt={`${config.sponsorBanner.sponsorName} logo`}
                    width={20}
                    height={20}
                    className="object-contain"
                  />
                </div>
              </div>
            )}
            
            {/* Content */}
            <div className="flex items-center flex-1 min-w-0 gap-2">
              <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                {t('sponsored')} {config.sponsorBanner.sponsorName}
              </span>
              <span className="text-sm text-gray-700 truncate hidden sm:inline">
                {t('text', { sponsorName: config.sponsorBanner.sponsorName })}
              </span>
            </div>
          </div>
          
          {/* Action Button */}
          <div className="flex items-center ml-4 flex-shrink-0">
            <a
              href={config.sponsorBanner.link}
              target="_blank"
              rel="sponsored nofollow noopener noreferrer"
              aria-label={`${t('sponsored')} ${config.sponsorBanner.sponsorName} - ${t('linkText')}`}
              className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-medium rounded-md hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-sm hover:shadow-md whitespace-nowrap"
            >
              <span>{t('linkText')}</span>
              <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </aside>
  );
}
